import { Trans } from "@lingui/react/macro";
import {
  informationFieldConfigValueSchema,
  patientKeys,
  updateFlags,
} from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import TextArea from "@/components/TextArea/TextArea";
import { useLingui } from "@lingui/react/macro";
import type z from "zod";

export const EditFlags = ({
  currentFlags,
  onSuccess,
  patientId,
  fieldConfig,
}: {
  currentFlags: string | null | undefined;
  onSuccess: () => void;
  patientId: string;
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      flags: currentFlags,
    },
  });

  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (flags: string | null | undefined) =>
      updateFlags(patientId, flags),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.flags);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar information om flaggningar`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : undefined}
          <Form.Row>
            <TextArea
              label={t`Flaggningar`}
              showOptionalLabel={
                fieldConfig === informationFieldConfigValueSchema.enum.optional
              }
              errorMessage={errors.flags?.message}
              {...register("flags", {
                required: {
                  value:
                    fieldConfig ===
                    informationFieldConfigValueSchema.enum.required,
                  message: t`Ange information om flaggningar`,
                },
              })}
            />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
